import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Bot, User, Sparkles, Loader2, Send, FolderPlus, PlusCircle, History, X, AlertTriangle } from 'lucide-react';
import type { EcadAutomacaoPasso, Subtopico, Perfil } from '../types';

interface EcadCopilotDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  copilotMessages: Array<{ role: 'user' | 'assistant', text: string }>;
  copilotInput: string;
  setCopilotInput: (val: string) => void;
  copilotLoading: boolean;
  handleSendCopilotMessage: (
    overrideMsg?: string,
    overrideFlags?: {
      includePerfil?: boolean;
      includeTopico?: boolean;
      includeSubtopico?: boolean;
      includePassos?: boolean;
    },
    overrideRoteiro?: Subtopico
  ) => Promise<void>;
  handleApplyAiSteps: (steps: EcadAutomacaoPasso[]) => void;
  handleApplyProposedHierarchy: (checkedItems: Array<{ topicTitle: string; subtopicTitle: string; subtopicDesc: string; passos?: any[] }>) => Promise<void>;
  proposedUiActions: any[] | null;
  onConfirmUiActions: () => void;
  onRejectUiActions: () => void;
  activeEditingIndex: number | null;
  selectedStepIndexes: number[];
  includePerfilContext: boolean;
  setIncludePerfilContext: (val: boolean) => void;
  includeTopicoContext: boolean;
  setIncludeTopicoContext: (val: boolean) => void;
  includeSubtopicoContext: boolean;
  setIncludeSubtopicoContext: (val: boolean) => void;
  includePassosContext: boolean;
  setIncludePassosContext: (val: boolean) => void;
  selectedRoteiro: Subtopico | null;
  passos: EcadAutomacaoPasso[];
  activeStepOrdem: number | null;
  activeTab: 'editor' | 'midias' | 'simulacao' | 'restaurar';
  simulationLogs: string[];
  perfis: Perfil[];
  selectedPerfilId: string | null;
  selectedTopicoId: string | null;
  handleApplyTopicOrder: (orderedIds: string[]) => void;
  handleRenameEcadItem: (itemType: 'TOPIC' | 'SUBTOPIC', itemId: string, newTitle: string) => Promise<void>;
  handleArchiveEcadItem: (itemType: 'TOPIC' | 'SUBTOPIC', itemId: string) => Promise<void>;
  onNewSession?: () => void;
  sessionHistory?: Array<{ id: string; label: string; timestamp: string; messages: any[] }>;
}

interface ContentBlock {
  type: 'text' | 'code';
  content: string;
  language?: string;
}

const parseMessageText = (text: string): ContentBlock[] => {
  const parts = text.split(/(```[\s\S]*?```)/g);
  return parts.map(part => {
    if (part.startsWith('```') && part.endsWith('```')) {
      const firstLineBreak = part.indexOf('\n');
      const lang = part.substring(3, firstLineBreak !== -1 ? firstLineBreak : 3).trim();
      const code = part.substring(firstLineBreak !== -1 ? firstLineBreak + 1 : 3, part.length - 3);
      return {
        type: 'code' as const,
        content: code,
        language: lang || 'code'
      };
    }
    return {
      type: 'text' as const,
      content: part
    };
  }).filter(p => p.content.length > 0);
};

const CollapsibleCodeBlock: React.FC<{
  code: string;
  language?: string;
  defaultCollapsed?: boolean;
}> = ({ code, language, defaultCollapsed = false }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div style={{
      margin: '8px 0',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div 
        onClick={() => setCollapsed(!collapsed)}
        style={{
          padding: '6px 12px',
          background: '#f1f5f9',
          borderBottom: collapsed ? 'none' : '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', fontFamily: 'monospace' }}>
          Código: {language || 'texto'}
        </span>
        <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700 }}>
          {collapsed ? 'Expandir ↴' : 'Recolher ⬁'}
        </span>
      </div>

      {/* Code Area */}
      {!collapsed && (
        <pre style={{
          margin: 0,
          padding: '10px 12px',
          fontSize: '0.72rem',
          fontFamily: 'Consolas, Monaco, monospace',
          color: '#0f172a',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          maxHeight: '260px',
          overflowY: 'auto',
          background: '#ffffff'
        }}>
          {code.trim()}
        </pre>
      )}
    </div>
  );
};

const ProposedHierarchyChecklist: React.FC<{
  hierarchy: Array<{
    titulo: string;
    subtopicos: Array<{ titulo: string; descricao: string; passos?: any[] }>;
  }>;
  onApply: (checkedItems: Array<{ topicTitle: string; subtopicTitle: string; subtopicDesc: string; passos?: any[] }>) => Promise<void>;
}> = ({ hierarchy, onApply }) => {
  const [checkedState, setCheckedState] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    hierarchy.forEach((topic, tIdx) => {
      topic.subtopicos.forEach((sub, sIdx) => {
        initial[`${tIdx}-${sIdx}`] = true;
      });
    });
    return initial;
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleSubtopic = (tIdx: number, sIdx: number) => {
    setCheckedState(prev => ({
      ...prev,
      [`${tIdx}-${sIdx}`]: !prev[`${tIdx}-${sIdx}`]
    }));
  };

  const toggleTopic = (tIdx: number, allSubtopicsCount: number) => {
    const keyPrefix = `${tIdx}-`;
    const subtopicKeys = Object.keys(checkedState).filter(k => k.startsWith(keyPrefix));
    const allChecked = subtopicKeys.every(k => checkedState[k]);

    setCheckedState(prev => {
      const next = { ...prev };
      for (let sIdx = 0; sIdx < allSubtopicsCount; sIdx++) {
        next[`${tIdx}-${sIdx}`] = !allChecked;
      }
      return next;
    });
  };

  const getSelectedItems = () => {
    const selected: Array<{ topicTitle: string; subtopicTitle: string; subtopicDesc: string; passos?: any[] }> = [];
    hierarchy.forEach((topic, tIdx) => {
      topic.subtopicos.forEach((sub, sIdx) => {
        if (checkedState[`${tIdx}-${sIdx}`]) {
          selected.push({
            topicTitle: topic.titulo,
            subtopicTitle: sub.titulo,
            subtopicDesc: sub.descricao,
            passos: (sub as any).passos
          });
        }
      });
    });
    return selected;
  };

  const handleApply = async () => {
    const selected = getSelectedItems();
    if (selected.length === 0) {
      toast.warning('Selecione pelo menos um roteiro para aplicar.');
      return;
    }
    setLoading(true);
    try {
      await onApply(selected);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao criar os tópicos e subtópicos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      marginTop: '12px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <FolderPlus size={14} /> Planejamento de Estrutura
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', paddingRight: '4px' }}>
        {hierarchy.map((topic, tIdx) => {
          const subKeys = topic.subtopicos.map((_, sIdx) => `${tIdx}-${sIdx}`);
          const checkedCount = subKeys.filter(k => checkedState[k]).length;
          const isAllChecked = checkedCount === topic.subtopicos.length;
          const isSomeChecked = checkedCount > 0 && checkedCount < topic.subtopicos.length;

          return (
            <div key={tIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  background: '#f1f5f9'
                }}
                onClick={() => toggleTopic(tIdx, topic.subtopicos.length)}
              >
                <input 
                  type="checkbox"
                  checked={isAllChecked}
                  ref={el => {
                    if (el) {
                      el.indeterminate = isSomeChecked;
                    }
                  }}
                  onChange={() => {}}
                  style={{ cursor: 'pointer', accentColor: '#7c3aed' }}
                />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>
                  📁 {topic.titulo}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '16px' }}>
                {topic.subtopicos.map((sub, sIdx) => {
                  const isChecked = !!checkedState[`${tIdx}-${sIdx}`];
                  return (
                    <div 
                      key={sIdx} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '8px', 
                        cursor: 'pointer',
                        padding: '2px 4px'
                      }}
                      onClick={() => toggleSubtopic(tIdx, sIdx)}
                    >
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        style={{ cursor: 'pointer', marginTop: '2px', accentColor: '#7c3aed' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.7rem', color: isChecked ? '#1e293b' : '#64748b', fontWeight: isChecked ? 600 : 400 }}>
                          📄 {sub.titulo}
                        </span>
                        {sub.descricao && (
                          <span style={{ fontSize: '0.62rem', color: '#64748b' }}>
                            {sub.descricao}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {success ? (
        <div style={{
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '6px',
          padding: '8px',
          color: '#059669',
          fontSize: '0.7rem',
          fontWeight: 700,
          textAlign: 'center'
        }}>
          ✓ Tópicos e roteiros criados com sucesso!
        </div>
      ) : (
        <button
          onClick={handleApply}
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 10px',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.72rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? (
            <>
              <Loader2 size={12} className="spin" />
              Aplicando marcados...
            </>
          ) : (
            <>
              <Sparkles size={12} />
              Aplicar Marcados
            </>
          )}
        </button>
      )}
    </div>
  );
};

export const EcadCopilotDrawer: React.FC<EcadCopilotDrawerProps> = ({
  isOpen,
  onClose,
  copilotMessages,
  copilotInput,
  setCopilotInput,
  copilotLoading,
  handleSendCopilotMessage,
  handleApplyAiSteps,
  handleApplyProposedHierarchy,
  proposedUiActions,
  onConfirmUiActions,
  onRejectUiActions,
  activeEditingIndex,
  selectedStepIndexes,
  includePerfilContext,
  setIncludePerfilContext,
  includeTopicoContext,
  setIncludeTopicoContext,
  includeSubtopicoContext,
  setIncludeSubtopicoContext,
  includePassosContext,
  setIncludePassosContext,
  selectedRoteiro,
  passos,
  activeStepOrdem,
  activeTab,
  simulationLogs,
  perfis,
  selectedPerfilId,
  selectedTopicoId,
  handleApplyTopicOrder,
  handleRenameEcadItem,
  handleArchiveEcadItem,
  onNewSession,
  sessionHistory
}) => {
  const activePerfil = perfis.find(p => p.id === selectedPerfilId);
  const activeTopico = activePerfil?.topicos.find(t => t.id === selectedTopicoId);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [copilotInput]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [copilotMessages, copilotLoading, simulationLogs, isOpen]);

  const parseJsonSteps = (text: string): EcadAutomacaoPasso[] | null => {
    try {
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        const parsed = JSON.parse(match[1]);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].tipoAcao !== undefined) {
          return parsed;
        }
      }
    } catch (e) {}
    return null;
  };

  const parseProposedHierarchy = (text: string): any[] | null => {
    try {
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        const parsed = JSON.parse(match[1]);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].subtopicos !== undefined) {
          return parsed;
        }
      }
      const jsonMatch = text.match(/\[\s*\{\s*"titulo"[\s\S]*?\}\s*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].subtopicos !== undefined) {
          return parsed;
        }
      }
    } catch (e) {}
    return null;
  };

  const parseProposedControl = (text: string): any | null => {
    try {
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        const parsed = JSON.parse(match[1]);
        if (parsed && typeof parsed === 'object' && parsed.controlType !== undefined) {
          return parsed;
        }
      }
      const jsonMatch = text.match(/\{\s*"controlType"[\s\S]*?\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed && typeof parsed === 'object' && parsed.controlType !== undefined) {
          return parsed;
        }
      }
    } catch (e) {}
    return null;
  };

  return (
    <>
      {/* Backdrop Overlay */}
      {isOpen && (
        <div 
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.15)',
            backdropFilter: 'blur(4px)',
            zIndex: 9998,
            animation: 'fadeInOverlay 0.25s ease-out'
          }}
        />
      )}

      {/* Drawer Body */}
      <aside style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '380px',
        maxWidth: '90vw',
        background: '#ffffff',
        borderLeft: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: "'Plus Jakarta Sans', sans-serif"
      }}>
        {/* Drawer Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Bot size={20} style={{ color: '#7c3aed' }} />
          <div>
            <h3 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
              {activeTab === 'simulacao' ? 'Copiloto Runtime' : 'Copiloto AI Studio'}
            </h3>
            <p style={{ fontSize: '0.68rem', color: '#64748b', margin: 0 }}>
              Gemini 2.5 Flash ativado
            </p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            {onNewSession && (
              <button
                onClick={onNewSession}
                title="Nova Sessão"
                style={{
                  background: 'rgba(124, 58, 237, 0.06)',
                  border: '1px solid rgba(124, 58, 237, 0.2)',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '0.68rem',
                  color: '#7c3aed',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                <PlusCircle size={12} />
                Nova
              </button>
            )}
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          background: '#f8fafc'
        }}>
          {(() => {
            const adjustedMessages = copilotMessages.map((msg, idx) => {
              let txt = typeof msg.text === 'string' ? msg.text : String(msg.text || '');
              if (activeTab === 'simulacao' && idx === 0 && msg.role === 'assistant') {
                txt = 'Monitorando simulador...';
              }
              return { ...msg, text: txt };
            });

            const displayList: Array<{ role: 'user' | 'assistant' | 'system-log', text: string }> = [...adjustedMessages];
            
            if (activeTab === 'simulacao' && simulationLogs && simulationLogs.length > 0) {
              const logMessages = simulationLogs.map((log) => ({
                role: 'system-log' as const,
                text: typeof log === 'string' ? log : String(log || '')
              }));
              displayList.splice(1, 0, ...logMessages);
            }

            return displayList.map((msg, index) => {
              const isUser = msg.role === 'user';
              const isLog = msg.role === 'system-log';
              const aiSteps = !isUser && !isLog ? parseJsonSteps(msg.text) : null;
              const proposedHierarchy = !isUser && !isLog ? parseProposedHierarchy(msg.text) : null;
              const proposedControl = !isUser && !isLog ? parseProposedControl(msg.text) : null;

              if (isLog) {
                let logColor = '#475569';
                let logBg = '#ffffff';
                let logBorder = '#e2e8f0';
                
                if (msg.text.includes('❌') || msg.text.includes('erro') || msg.text.includes('FAILED')) {
                  logColor = '#b91c1c';
                  logBg = '#fef2f2';
                  logBorder = '#fca5a5';
                } else if (msg.text.includes('🟢') || msg.text.includes('Início') || msg.text.includes('esperada')) {
                  logColor = '#6d28d9';
                  logBg = '#f5f3ff';
                  logBorder = '#ddd6fe';
                } else if (msg.text.includes('✅') || msg.text.includes('Fim') || msg.text.includes('Concluído') || msg.text.includes('Gatilho')) {
                  logColor = '#047857';
                  logBg = '#f0fdf4';
                  logBorder = '#bbf7d0';
                }

                return (
                  <div 
                    key={index}
                    style={{
                      alignSelf: 'stretch',
                      background: logBg,
                      border: `1px solid ${logBorder}`,
                      borderRadius: '8px',
                      padding: '6px 10px',
                      color: logColor,
                      fontSize: '0.72rem',
                      fontFamily: 'monospace',
                      lineHeight: 1.35,
                      wordBreak: 'break-all',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.01)'
                    }}
                  >
                    <span style={{ color: '#94a3b8', flexShrink: 0 }}>&gt;</span>
                    <span style={{ flex: 1 }}>{msg.text}</span>
                  </div>
                );
              }

              const hasControl = !!proposedHierarchy || !!proposedControl || !!aiSteps;

              return (
                <div 
                  key={index}
                  style={{
                    alignSelf: isUser ? 'flex-end' : 'stretch',
                    maxWidth: isUser ? '85%' : '100%',
                    background: isUser ? 'rgba(124, 58, 237, 0.08)' : '#ffffff',
                    border: '1px solid',
                    borderColor: isUser ? 'rgba(124, 58, 237, 0.15)' : '#e2e8f0',
                    borderRadius: '12px',
                    padding: '10px 12px',
                    color: '#1e293b',
                    fontSize: '0.8rem',
                    lineHeight: 1.4,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontSize: '0.68rem', color: '#64748b', fontWeight: 700 }}>
                    {isUser ? <User size={12} /> : <Bot size={12} style={{ color: '#7c3aed' }} />}
                    {isUser ? 'Você' : 'Copiloto AI'}
                  </div>
                  
                  <div>
                    {parseMessageText(msg.text).map((block, bIdx) => {
                      if (block.type === 'code') {
                        return (
                          <CollapsibleCodeBlock 
                            key={bIdx}
                            code={block.content}
                            language={block.language}
                            defaultCollapsed={hasControl}
                          />
                        );
                      }
                      return (
                        <div key={bIdx} style={{ whiteSpace: 'pre-wrap' }}>
                          {block.content}
                        </div>
                      );
                    })}
                  </div>

                  {aiSteps && (
                    <div style={{ marginTop: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                      <button
                        onClick={() => handleApplyAiSteps(aiSteps)}
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)'
                        }}
                      >
                        <Sparkles size={12} />
                        Aplicar Roteiro da IA ({aiSteps.length} passos)
                      </button>
                    </div>
                  )}

                  {proposedHierarchy && (
                    <ProposedHierarchyChecklist 
                      hierarchy={proposedHierarchy}
                      onApply={handleApplyProposedHierarchy}
                    />
                  )}

                  {proposedControl && proposedControl.controlType === 'MOVE_TOPICS' && (
                    <div style={{
                      marginTop: '12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        ↕ Reordenar Tópicos
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                        A IA sugeriu reordenar os tópicos conforme abaixo:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: '#ffffff', border: '1px solid #e2e8f0', padding: '8px', borderRadius: '6px' }}>
                        {proposedControl.orderedTopicIds?.map((id: string, idx: number) => {
                          const topic = activePerfil?.topicos.find(t => t.id === id);
                          return (
                            <div key={id} style={{ fontSize: '0.72rem', color: '#1e293b', display: 'flex', gap: '6px' }}>
                              <span style={{ color: '#7c3aed', fontWeight: 700 }}>{idx + 1}.</span>
                              <span>{topic?.titulo || id}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: '0.62rem', color: '#64748b', fontStyle: 'italic' }}>
                        ⚠️ Nenhuma alteração será feita nos dados, apenas na ordem visual de exibição.
                      </div>
                      <button
                        onClick={() => handleApplyTopicOrder(proposedControl.orderedTopicIds)}
                        style={{
                          background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        Aplicar Nova Ordem
                      </button>
                    </div>
                  )}

                  {proposedControl && proposedControl.controlType === 'RENAME_ITEM' && (
                    <div style={{
                      marginTop: '12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        ✏ Renomear Item ({proposedControl.itemType === 'TOPIC' ? 'Tópico' : 'Roteiro'})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '0.68rem', color: '#475569' }}>
                          Título atual: <span style={{ textDecoration: 'line-through', color: '#ef4444' }}>{proposedControl.oldTitle}</span>
                        </span>
                        <span style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 700 }}>
                          Novo título: {proposedControl.newTitle}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRenameEcadItem(proposedControl.itemType, proposedControl.itemId, proposedControl.newTitle)}
                        style={{
                          background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        Confirmar Renomeação
                      </button>
                    </div>
                  )}

                  {proposedControl && proposedControl.controlType === 'ARCHIVE_ITEM' && (
                    <div style={{
                      marginTop: '12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        📦 Arquivar Item ({proposedControl.itemType === 'TOPIC' ? 'Tópico' : 'Roteiro'})
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                        O item <strong>{proposedControl.title}</strong> será arquivado e ocultado do catálogo principal. Ele poderá ser recuperado na pasta de Arquivados.
                      </div>
                      <button
                        onClick={() => handleArchiveEcadItem(proposedControl.itemType, proposedControl.itemId)}
                        style={{
                          background: '#ef4444',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        Confirmar Arquivamento
                      </button>
                    </div>
                  )}
                </div>
              );
            });
          })()}
          {copilotLoading && (
            <div style={{
              alignSelf: 'flex-start',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#64748b',
              fontSize: '0.75rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
            }}>
              <Loader2 size={12} className="spin" /> Alice está elaborando os roteiros...
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Context Active Toggle Chips */}
        <div style={{
          padding: '8px 14px',
          borderTop: '1px solid #e2e8f0',
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {(() => {
            const showPerfil = activePerfil && activePerfil.nome && activePerfil.nome.toLowerCase() !== 'nenhum';
            const showTopico = activeTopico && activeTopico.titulo && activeTopico.titulo.toLowerCase() !== 'nenhum';
            const showSubtopico = selectedRoteiro && selectedRoteiro.titulo && selectedRoteiro.titulo.toLowerCase() !== 'nenhum';
            const isCustomState = activeEditingIndex !== null || (activeTab === 'simulacao' && activeStepOrdem !== null) || selectedStepIndexes.length > 0;
            const showPassos = isCustomState || passos.length > 0;

            if (!showPerfil && !showTopico && !showSubtopico && !showPassos) {
              return (
                <div style={{ fontSize: '0.68rem', color: '#64748b', fontStyle: 'italic', textAlign: 'center', padding: '4px 0' }}>
                  Nenhum contexto selecionado
                </div>
              );
            }

            const chipStyle = (isActive: boolean): React.CSSProperties => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: '12px',
              fontSize: '0.65rem',
              fontWeight: 700,
              cursor: 'pointer',
              border: '1px solid',
              background: isActive ? 'rgba(16, 185, 129, 0.08)' : '#f1f5f9',
              borderColor: isActive ? 'rgba(16, 185, 129, 0.2)' : '#e2e8f0',
              color: isActive ? '#059669' : '#64748b',
              transition: 'all 0.2s',
              outline: 'none',
              whiteSpace: 'nowrap',
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textDecoration: isActive ? 'none' : 'line-through',
              opacity: isActive ? 1 : 0.6,
            });

            const siglaStyle = (isActive: boolean): React.CSSProperties => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '18px',
              height: '18px',
              borderRadius: '4px',
              fontSize: '0.58rem',
              fontWeight: 800,
              background: isActive ? 'rgba(16, 185, 129, 0.15)' : '#e2e8f0',
              color: isActive ? '#059669' : '#64748b',
              flexShrink: 0,
              letterSpacing: '-0.5px',
            });

            const perfilWord = (activePerfil?.nome || '').split(' ').pop()?.toUpperCase() || 'PE';
            const topicoIdx = activePerfil?.topicos?.findIndex((t: any) => t.id === activeTopico?.id);
            const topicoNum = topicoIdx !== undefined && topicoIdx >= 0 ? topicoIdx + 1 : 1;
            const subtopicoIdx = activeTopico?.subtopicos?.findIndex((s: any) => s.id === selectedRoteiro?.id);
            const subtopicoNum = subtopicoIdx !== undefined && subtopicoIdx >= 0 ? subtopicoIdx + 1 : 1;

            const passosLabel = selectedStepIndexes.length > 0
              ? selectedStepIndexes.map(i => `#${i + 1}`).join(',')
              : activeEditingIndex !== null
                ? `#${activeEditingIndex + 1}`
                : (activeTab === 'simulacao' && activeStepOrdem !== null)
                  ? `#${activeStepOrdem}`
                  : `${passos.length}`;

            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
                {showPerfil && (
                  <button
                    onClick={() => setIncludePerfilContext(!includePerfilContext)}
                    style={chipStyle(includePerfilContext)}
                    title={`Contexto Perfil: ${activePerfil?.nome}`}
                  >
                    <span style={siglaStyle(includePerfilContext)}>PE</span>
                    {perfilWord}
                  </button>
                )}
                {showTopico && (
                  <button
                    onClick={() => setIncludeTopicoContext(!includeTopicoContext)}
                    style={chipStyle(includeTopicoContext)}
                    title={`Contexto Tópico: ${activeTopico?.titulo}`}
                  >
                    <span style={siglaStyle(includeTopicoContext)}>TO</span>
                    {topicoNum}
                  </button>
                )}
                {showSubtopico && (
                  <button
                    onClick={() => setIncludeSubtopicoContext(!includeSubtopicoContext)}
                    style={chipStyle(includeSubtopicoContext)}
                    title={`Contexto Roteiro: ${selectedRoteiro?.titulo}`}
                  >
                    <span style={siglaStyle(includeSubtopicoContext)}>RO</span>
                    {topicoNum}.{subtopicoNum}
                  </button>
                )}
                {showPassos && (
                  <button
                    onClick={() => setIncludePassosContext(!includePassosContext)}
                    style={chipStyle(includePassosContext)}
                    title="Contexto Passos de Automação"
                  >
                    <span style={siglaStyle(includePassosContext)}>PA</span>
                    {passosLabel}
                  </button>
                )}
              </div>
            );
          })()}

          {includePassosContext && (activeEditingIndex !== null || selectedStepIndexes.length > 0 || (activeTab === 'simulacao' && activeStepOrdem !== null)) && (
            <div style={{
              fontSize: '0.68rem',
              color: '#334155',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '8px 10px',
              lineHeight: 1.45,
              marginTop: '4px'
            }}>
              {activeEditingIndex !== null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div>
                    <strong>Passo {activeEditingIndex + 1} ({passos[activeEditingIndex]?.tipoAcao}):</strong>{' '}
                    <span style={{ color: '#475569' }}>
                      {passos[activeEditingIndex]?.legenda || 'Sem legenda'}
                    </span>
                  </div>
                </div>
              ) : (activeTab === 'simulacao' && activeStepOrdem !== null) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div>
                    <strong>Passo {activeStepOrdem} ({passos[activeStepOrdem - 1]?.tipoAcao}):</strong>{' '}
                    <span style={{ color: '#475569' }}>
                      {passos[activeStepOrdem - 1]?.legenda || 'Sem legenda'}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {selectedStepIndexes.sort((a, b) => a - b).map(idx => (
                    <span 
                      key={idx} 
                      style={{ 
                        background: '#f1f5f9', 
                        padding: '1px 4px', 
                        borderRadius: '3px',
                        border: '1px solid #e2e8f0' 
                      }}
                    >
                      #{idx + 1}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Proposed UI Actions Confirm Panel */}
        {proposedUiActions && proposedUiActions.length > 0 && (
          <div style={{
            padding: '12px 14px',
            borderTop: '1px solid rgba(124, 58, 237, 0.3)',
            background: 'rgba(124, 58, 237, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#7c3aed', fontWeight: 800 }}>
              <Sparkles size={13} /> Ações sugeridas pela IA:
            </div>
            <ul style={{
              margin: 0,
              paddingLeft: '16px',
              fontSize: '0.68rem',
              color: '#334155',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              {proposedUiActions.map((act, idx) => {
                let desc = '';
                let color = '#334155';
                let icon = '';
                if (act.action === 'SCROLL') { desc = `Rolar até o Passo ${act.index + 1}`; icon = '📜'; }
                else if (act.action === 'HIGHLIGHT') { desc = `Destacar o Passo ${act.index + 1}`; icon = '✨'; }
                else if (act.action === 'SELECT') { desc = `Selecionar os passos: ${Array.isArray(act.indexes) ? act.indexes.map((i: number) => `#${i + 1}`).join(', ') : ''}`; icon = '🎯'; }
                else if (act.action === 'EDIT') { desc = `Editar o Passo ${act.index + 1}`; icon = '✏️'; color = '#b45309'; }
                else if (act.action === 'DELETE') { desc = `Excluir ${Array.isArray(act.indexes) ? act.indexes.length : 0} passo(s)`; icon = '🗑️'; color = '#dc2626'; }
                else if (act.action === 'ADD') { desc = `Adicionar passo no final: ${act.fields?.tipoAcao || 'EXPLICAR'}`; icon = '➕'; color = '#059669'; }
                else if (act.action === 'INSERT_AT') { desc = `Inserir ${Array.isArray(act.steps) ? act.steps.length : 0} passo(s) na posição #${act.index + 1}`; icon = '📥'; color = '#2563eb'; }
                else if (act.action === 'REPLACE_RANGE') { desc = `Substituir passos #${act.fromIndex + 1} a #${act.toIndex + 1}`; icon = '🔄'; color = '#7c3aed'; }
                else if (act.action === 'MOVE') { desc = `Mover passo #${act.fromIndex + 1} para posição #${act.toIndex + 1}`; icon = '↕️'; color = '#b45309'; }

                return <li key={idx} style={{ color, display: 'flex', alignItems: 'center', gap: '4px' }}><span>{icon}</span> {desc}</li>;
              })}
            </ul>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button
                onClick={onConfirmUiActions}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)'
                }}
              >
                Executar Ações
              </button>
              <button
                onClick={onRejectUiActions}
                style={{
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  cursor: 'pointer'
                }}
              >
                Descartar
              </button>
            </div>
          </div>
        )}

        {/* Chat Input */}
        <div style={{
          padding: '12px',
          borderTop: '1px solid #e2e8f0',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          <textarea 
            ref={textareaRef}
            id="copilot-drawer-input"
            name="copilot-drawer-input"
            autoComplete="off"
            rows={1}
            className="copilot-textarea"
            placeholder="Pergunte à Alice Copiloto..."
            value={copilotInput}
            onChange={(e) => setCopilotInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!copilotLoading && copilotInput.trim()) {
                  handleSendCopilotMessage();
                }
              }
            }}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              color: '#1e293b',
              fontSize: '0.8rem',
              outline: 'none',
              fontWeight: 500,
              resize: 'none',
              minHeight: '38px',
              maxHeight: '120px',
              overflowY: 'auto',
              lineHeight: '1.4',
              boxSizing: 'border-box'
            }}
          />
          <button
            onClick={() => handleSendCopilotMessage()}
            disabled={copilotLoading || !copilotInput.trim()}
            style={{
              background: 'rgba(124, 58, 237, 0.08)',
              border: '1px solid rgba(124, 58, 237, 0.25)',
              borderRadius: '8px',
              padding: '10px',
              color: '#7c3aed',
              cursor: (copilotLoading || !copilotInput.trim()) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '38px',
              width: '38px',
              boxSizing: 'border-box',
              flexShrink: 0
            }}
          >
            <Send size={14} />
          </button>
        </div>
      </aside>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .copilot-textarea::-webkit-scrollbar {
          display: none !important;
        }
      `}} />
    </>
  );
};
